import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge, EmptyState } from "../components/common";
import { JOB_STATUSES, eur } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, MapPin } from "lucide-react";

export default function Lavori() {
  const { can } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ status: "sopralluogo" });

  const load = async () => { const { data } = await api.get(`/jobs${filter ? `?status=${filter}` : ""}`); setItems(data); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => { if (can("view_clients")) api.get("/clients").then((r) => setClients(r.data)); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!form.client_id) return toast.error("Seleziona il cliente");
    try {
      await api.post("/jobs", { ...form, materials: (form.materials_txt||"").split(",").map(s=>s.trim()).filter(Boolean) });
      toast.success("Lavoro creato"); setOpen(false); setForm({ status: "sopralluogo" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito. I dati inseriti sono stati mantenuti."); }
  };

  return (
    <div>
      <PageHeader title={can("view_jobs_all") ? "Lavori" : "I miei lavori"} subtitle={`${items.length} lavori`}>
        {can("edit_jobs") && <Button className="bg-brand hover:bg-[#A8472F] text-white" data-testid="btn-new-job" onClick={() => { setForm({ status: "sopralluogo" }); setOpen(true); }}><Plus size={16} className="mr-1" /> Nuovo lavoro</Button>}
      </PageHeader>

      <div className="flex gap-1.5 flex-wrap mb-5">
        <button onClick={() => setFilter("")} className={`text-xs px-3 py-1.5 rounded-full border ${!filter ? "bg-ink text-white" : "bg-white text-muted-foreground"}`}>Tutti</button>
        {JOB_STATUSES.map((s) => <button key={s.value} onClick={() => setFilter(s.value)} className={`text-xs px-3 py-1.5 rounded-full border ${filter === s.value ? "bg-ink text-white" : "bg-white text-muted-foreground"}`}>{s.label}</button>)}
      </div>

      {items.length === 0 ? <EmptyState title="Nessun lavoro" /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((j) => (
            <button key={j.id} data-testid={`job-card-${j.id}`} onClick={() => nav(`/lavori/${j.id}`)} className="card-soft p-4 text-left lift">
              <div className="flex items-start justify-between gap-2">
                <p className="font-head font-bold">{j.product}</p>
              </div>
              <p className="text-sm text-muted-foreground mt-1">{j.client_name}</p>
              {j.site_address && <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><MapPin size={12} /> {j.site_address}</p>}
              <div className="flex items-center justify-between mt-3">
                <StatusBadge list={JOB_STATUSES} value={j.status} />
                {can("view_prices") && <span className="text-sm font-semibold">{eur(j.agreed_amount)}</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="job-dialog">
          <DialogHeader><DialogTitle className="font-head">Nuovo lavoro</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Cliente *</Label>
              <Select value={form.client_id || ""} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="job-client"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>{clients.filter((c)=>c.id).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Prodotto</Label><Input data-testid="job-product" value={form.product||""} onChange={(e)=>setForm({...form,product:e.target.value})} /></div>
            <div><Label>Descrizione</Label><Textarea value={form.description||""} onChange={(e)=>setForm({...form,description:e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Misure</Label><Input value={form.measures||""} onChange={(e)=>setForm({...form,measures:e.target.value})} /></div>
              <div><Label>Importo €</Label><Input type="number" value={form.agreed_amount||""} onChange={(e)=>setForm({...form,agreed_amount:e.target.value})} /></div>
            </div>
            <div><Label>Indirizzo cantiere</Label><Input value={form.site_address||""} onChange={(e)=>setForm({...form,site_address:e.target.value})} /></div>
            <div><Label>Materiali da portare (separati da virgola)</Label><Input value={form.materials_txt||""} onChange={(e)=>setForm({...form,materials_txt:e.target.value})} /></div>
            <div><Label>Stato</Label>
              <Select value={form.status} onValueChange={(v)=>setForm({...form,status:v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{JOB_STATUSES.map((s)=><SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Annulla</Button><Button className="bg-brand text-white" data-testid="job-save" onClick={save}>Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
